import { useToast } from '@lifebit-ai/lds'
import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { extractErrorMessage } from 'core/utils'
import { getImportRequestsListPath } from 'modules/requests/requestDetailPaths'
import { validateResultLabel } from 'modules/resultsV2/labelValidation'
import { createImportRequestLabel, deleteImportRequestLabel, getImportRequestLabels } from 'services/import-requests'
import { ImportRequestStatus } from 'services/import-requests/enums'
import { useApproveImportRequest } from 'services/import-requests/hooks/useApproveImportRequest'
import { useGetImportRequestVerification } from 'services/import-requests/hooks/useGetImportRequestVerification'
import {
  type RejectImportRequestParams,
  useRejectImportRequest
} from 'services/import-requests/hooks/useRejectImportRequest'
import {
  getImportRequestApprovalModalDescription,
  getImportRequestDetailsTitle,
  getImportRequestRejectionModalDescription,
  isExternalImportRequestDetail,
  isImportRequestAwaitingManagerDecision,
  isImportRequestInFlight,
  isUploadedImportRequestDetail
} from 'services/import-requests/importRequestUtils'
import { ImportRequestQueryKeys } from 'services/import-requests/queryKeys'
import type { GetImportRequestLabelsResponse, ImportRequestVerificationDetail } from 'services/import-requests/types'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import { ApiErrorResponse } from 'services/types'
import { useGetUserInfo } from 'services/user/hooks/useGetUserInfo'
import { useGetWorkspaceConfiguration } from 'services/workspaces/v2/hooks/useGetWorkspaceConfiguration'
import { hasWorkspaceConfiguration } from 'services/workspaces/v2/utils'

const IMPORT_REQUEST_DETAILS_REFETCH_INTERVAL_MS = 5000

const formatFileSize = (bytes: number): string => {
  const kb = bytes / 1024
  return kb < 1 ? `${bytes} B` : `${kb.toFixed(1)} KB`
}

const shouldPollImportRequest = (importRequest?: ImportRequestVerificationDetail): boolean =>
  importRequest !== undefined && isImportRequestInFlight(importRequest)

export const useImportRequestDetails = (): {
  approveRequest: () => void
  approveRequestMutation: UseMutationResult<void, ApiErrorResponse, void>
  closeApprovalModal: () => void
  closeRejectionModal: () => void
  closeViewFormModal: () => void
  formattedFileSize?: string
  goBack: () => void
  handleAddResultLabel: (name: string) => void
  handleDeleteResultLabel: (labelId: string) => void
  handleOnClickLabelMenuItem: (labelId: string) => void
  importRequest?: ImportRequestVerificationDetail
  approvalModalDescription: string
  isApprovalModalOpen: boolean
  isApprovalActionsVisible: boolean
  isApprovalRestricted: boolean
  isFailureAlertVisible: boolean
  isLoading: boolean
  isNotFound: boolean
  isRejectionModalOpen: boolean
  isViewFormModalOpen: boolean
  labelsQuery: UseQueryResult<GetImportRequestLabelsResponse, ApiErrorResponse>
  openApprovalModal: () => void
  openRejectionModal: () => void
  openViewFormModal: () => void
  pageTitle: string
  rejectRequest: (reason: string) => void
  rejectRequestMutation: UseMutationResult<void, ApiErrorResponse, RejectImportRequestParams>
  rejectionModalDescription: string
  requestId?: string
  validateResultLabel: typeof validateResultLabel
} => {
  const { requestId } = useParams<{ requestId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false)
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false)
  const [isViewFormModalOpen, setIsViewFormModalOpen] = useState(false)

  const {
    data: importRequest,
    isLoading,
    isError
  } = useGetImportRequestVerification({
    requestId: requestId ?? '',
    options: {
      refetchInterval: query =>
        shouldPollImportRequest(query.state.data) ? IMPORT_REQUEST_DETAILS_REFETCH_INTERVAL_MS : false
    }
  })

  const { data: userInfo } = useGetUserInfo()
  const workspaceConfigurationQuery = useGetWorkspaceConfiguration(importRequest?.workspaceId ?? '', {
    enabled: !!importRequest?.workspaceId
  })

  const labelsQuery = useQuery<GetImportRequestLabelsResponse, ApiErrorResponse>({
    queryKey: ImportRequestQueryKeys.Labels(),
    queryFn: ({ signal }) => getImportRequestLabels({ signal })
  })

  const goBack = (): void => {
    navigate(getImportRequestsListPath())
  }

  const formattedFileSize =
    importRequest !== undefined &&
    isUploadedImportRequestDetail(importRequest) &&
    importRequest.sizeInBytes !== undefined
      ? formatFileSize(importRequest.sizeInBytes)
      : undefined

  const pageTitle = importRequest ? getImportRequestDetailsTitle(importRequest) : 'Import request'

  const isUserTheRequester = userInfo?._id === importRequest?.requestingUser?._id

  const workspaceConfiguration = workspaceConfigurationQuery.data

  const isSelfReviewRestrictionEnabled =
    hasWorkspaceConfiguration(workspaceConfiguration) &&
    !!workspaceConfiguration.permissions.admins?.enableSelfReviewRestriction

  const isWorkspaceConfigPendingForRequester =
    isUserTheRequester && !!importRequest?.workspaceId && !workspaceConfigurationQuery.isSuccess

  const isApprovalRestricted = isUserTheRequester && isSelfReviewRestrictionEnabled

  const isApprovalActionsVisible =
    importRequest !== undefined &&
    isImportRequestAwaitingManagerDecision(importRequest) &&
    !isApprovalRestricted &&
    !isWorkspaceConfigPendingForRequester

  const isExternalRejection =
    importRequest !== undefined &&
    isExternalImportRequestDetail(importRequest) &&
    importRequest.status === ImportRequestStatus.Rejected

  const isFailureAlertVisible =
    (importRequest?.status === ImportRequestStatus.Rejected || importRequest?.status === ImportRequestStatus.Failed) &&
    !isExternalRejection

  const approvalModalDescription = importRequest
    ? getImportRequestApprovalModalDescription(importRequest)
    : 'Do you want to approve this import request?'

  const rejectionModalDescription = importRequest
    ? getImportRequestRejectionModalDescription(importRequest)
    : 'Enter a reason for the rejection; it will be visible to the requester and other Airlock Managers.'

  const approveRequestMutation = useApproveImportRequest({
    requestId: requestId ?? '',
    onSuccess: () => setIsApprovalModalOpen(false)
  })

  const rejectRequestMutation = useRejectImportRequest({
    requestId: requestId ?? '',
    onSuccess: () => setIsRejectionModalOpen(false)
  })

  const createLabelMutation = useMutation<void, ApiErrorResponse, string>({
    mutationFn: name => createImportRequestLabel(requestId ?? '', name),
    onSuccess: () => {
      toast({ message: 'Label added successfully' })
      queryClient.invalidateQueries({ queryKey: ImportRequestQueryKeys.Labels() })
      queryClient.invalidateQueries({ queryKey: ImportRequestQueryKeys.Verification(requestId ?? '') })
      invalidateRequestActivityQueries(queryClient)
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to add label') })
    }
  })

  const deleteLabelMutation = useMutation<void, ApiErrorResponse, string>({
    mutationFn: labelId => deleteImportRequestLabel(requestId ?? '', labelId),
    onSuccess: () => {
      toast({ message: 'Label removed successfully' })
      queryClient.invalidateQueries({ queryKey: ImportRequestQueryKeys.Labels() })
      queryClient.invalidateQueries({ queryKey: ImportRequestQueryKeys.Verification(requestId ?? '') })
      invalidateRequestActivityQueries(queryClient)
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to remove label') })
    }
  })

  const openApprovalModal = (): void => setIsApprovalModalOpen(true)
  const closeApprovalModal = (): void => setIsApprovalModalOpen(false)
  const openRejectionModal = (): void => setIsRejectionModalOpen(true)
  const closeRejectionModal = (): void => setIsRejectionModalOpen(false)
  const openViewFormModal = (): void => setIsViewFormModalOpen(true)
  const closeViewFormModal = (): void => setIsViewFormModalOpen(false)

  const approveRequest = (): void => {
    if (!requestId || approveRequestMutation.isPending) return
    approveRequestMutation.mutate()
  }

  const rejectRequest = (reason: string): void => {
    const trimmedReason = reason.trim()
    if (!requestId || !trimmedReason || rejectRequestMutation.isPending) return

    rejectRequestMutation.mutate({ reason: trimmedReason })
  }

  const handleAddResultLabel = (name: string): void => {
    const trimmedName = name.trim()
    if (!requestId || !trimmedName) return

    createLabelMutation.mutate(trimmedName)
  }

  const handleDeleteResultLabel = (labelId: string): void => {
    const trimmedLabelId = labelId.trim()
    if (!requestId || !trimmedLabelId) return

    deleteLabelMutation.mutate(trimmedLabelId)
  }

  const handleOnClickLabelMenuItem = (labelId: string): void => {
    const selectedLabel = labelsQuery.data?.data.find(label => label._id === labelId)
    if (selectedLabel) handleAddResultLabel(selectedLabel.name)
  }

  return {
    approveRequest,
    approvalModalDescription,
    approveRequestMutation,
    closeApprovalModal,
    closeRejectionModal,
    closeViewFormModal,
    formattedFileSize,
    goBack,
    handleAddResultLabel,
    handleDeleteResultLabel,
    handleOnClickLabelMenuItem,
    importRequest,
    isApprovalModalOpen,
    isApprovalActionsVisible,
    isApprovalRestricted,
    isFailureAlertVisible,
    isLoading,
    isNotFound: !isLoading && (isError || !importRequest),
    isRejectionModalOpen,
    isViewFormModalOpen,
    labelsQuery,
    openApprovalModal,
    openRejectionModal,
    openViewFormModal,
    pageTitle,
    rejectRequest,
    rejectRequestMutation,
    rejectionModalDescription,
    requestId,
    validateResultLabel
  }
}
